import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ForgotPasswordPage: React.FC = () => {
  const { requestPasswordReset } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await requestPasswordReset(email);
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ground">
      <div className="max-w-md w-full mx-4 sm:mx-auto space-y-8 p-6 sm:p-8 bg-white rounded-xl shadow-overlay">
        {/* Logo */}
        <div className="flex flex-col items-center cursor-pointer gap-2" onClick={() => navigate('/')}>
          <img src="/dig_no_name_logo.png" alt="dialledin.golf" className="h-20 w-auto object-contain" />
          <img src="/dig_name_only_white_line.png" alt="dialledin.golf" className="h-10 w-auto object-contain" />
          <p className="text-ink-3 text-sm mt-1">A machine measures your swing; we recommend clubs built for it</p>
        </div>

        <div>
          <h1 className="text-xl font-semibold text-gray-900 text-center">Reset your password</h1>
          <p className="mt-2 text-sm text-gray-500 text-center">
            Enter the email on your account and we'll send you a link to reset your password.
          </p>
        </div>

        {submitted ? (
          <div className="space-y-6">
            <div className="bg-green/10 border border-green-ink text-green-ink px-4 py-3 rounded-lg text-sm">
              If that email is registered, a reset link has been sent. Check your inbox (and spam folder).
            </div>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full py-3 px-4 text-white font-medium rounded-lg bg-green-ink hover:opacity-90 active:bg-green-ink transition-colors"
            >
              Back to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                id="email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-ink focus:border-transparent transition-all"
                placeholder="you@example.com"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 px-4 text-white font-medium rounded-lg transition-colors ${
                isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-ink hover:opacity-90 active:bg-green-ink'
              }`}
            >
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        )}

        {!submitted && (
          <div className="text-center text-sm text-gray-600">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-green-ink hover:text-green-ink font-medium"
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
